import { NavLink } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import {
  PresentationChartLineIcon,
  CalendarDaysIcon,
  BuildingOfficeIcon,
  MegaphoneIcon,
  UserGroupIcon,
  ArrowRightOnRectangleIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

const navItems = [
  { to: '/key-metrics', label: 'Key Metrics', icon: CalendarDaysIcon },
  { to: '/retention', label: 'Retention', icon: UserGroupIcon },
  { to: '/sales', label: 'Sales', icon: PresentationChartLineIcon },
  { to: '/account-dashboard', label: 'Account Dashboard', icon: BuildingOfficeIcon },
  { to: '/marketing', label: 'Marketing', icon: MegaphoneIcon },
];

interface TopNavProps {
  onToggleChat: () => void;
  chatOpen: boolean;
}

export default function TopNav({ onToggleChat, chatOpen }: TopNavProps) {
  const { user, logout } = useApp();

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((c) => c[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '??';

  return (
    <header className="fixed top-0 left-0 right-0 z-30 bg-eep-navy text-white">
      <div className="flex items-center h-14 px-4">
        {/* Logo */}
        <div className="flex-shrink-0 mr-6">
          <img src="/logo-dark.svg" alt="EncoreEstate Plans" className="h-7" />
        </div>

        {/* Nav links */}
        <nav className="flex items-center gap-1 flex-1 min-w-0">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm whitespace-nowrap transition-colors duration-150 ${
                  isActive
                    ? 'bg-eep-navy-light text-white font-medium'
                    : 'text-gray-300 hover:bg-eep-navy-light/50 hover:text-white'
                }`
              }
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="flex items-center gap-3 ml-4 flex-shrink-0">
          <button
            onClick={onToggleChat}
            className={`p-1.5 rounded-md transition-colors ${
              chatOpen
                ? 'bg-eep-green text-eep-navy'
                : 'text-gray-300 hover:bg-eep-navy-light hover:text-white'
            }`}
            title={chatOpen ? 'Close Assistant' : 'Open Assistant'}
          >
            <ChatBubbleLeftRightIcon className="w-5 h-5" />
          </button>
          <div className="w-px h-6 bg-white/20" />
          <div className="text-right hidden sm:block">
            <p className="text-[10px] text-gray-400 leading-tight">Welcome!</p>
            <p className="text-xs font-medium leading-tight">{user?.name || 'Loading...'}</p>
          </div>
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-8 h-8 rounded-full"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-eep-green text-eep-navy flex items-center justify-center text-xs font-bold">
              {initials}
            </div>
          )}
          <button
            onClick={logout}
            className="text-gray-400 hover:text-red-400 transition-colors"
            title="Sign out"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </header>
  );
}
